import { useState } from "react";
import { Layers, Archive, Plus, Pencil, Trash2, Briefcase, User, MoreHorizontal, Check, X, GripVertical, Share2, Users } from "lucide-react";
import { ShareAppLink } from "@/components/ShareApp";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TOP_ITEMS = [
  { key: "all", label: "All", Icon: Layers },
  { key: "completed", label: "Completed", Icon: Archive },
];

export default function Sidebar({ filter, setFilter, counts, lists, onCreateList, onUpdateList, onDeleteList, onReorderLists, onShareList, onShareWorkspace }) {
  const [renamingId, setRenamingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [menuOpenId, setMenuOpenId] = useState(null);

  // Split owned vs shared-with-me
  const ownedLists = lists.filter((l) => l.share_origin === "owner" || !l.share_origin);
  const sharedLists = lists.filter((l) => l.share_origin && l.share_origin !== "owner");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ownedLists.findIndex((l) => l.id === active.id);
    const newIndex = ownedLists.findIndex((l) => l.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(ownedLists, oldIndex, newIndex);
    onReorderLists(reordered.map((l) => l.id));
  };

  const startRename = (list) => {
    setRenamingId(list.id);
    setDraftName(list.name);
    setMenuOpenId(null);
  };

  const commitRename = async () => {
    const id = renamingId;
    const next = draftName.trim();
    setRenamingId(null);
    if (!id) return;
    const original = lists.find((l) => l.id === id);
    if (!next || (original && next === original.name)) return;
    await onUpdateList(id, { name: next });
  };

  const cancelRename = () => {
    setRenamingId(null);
    setDraftName("");
  };

  return (
    <aside className="w-full md:w-64 p-6 md:p-10 md:pr-6 border-b md:border-b-0 border-zinc-200" data-testid="sidebar">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4 hidden md:block">
        Views
      </p>
      <nav className="grid grid-cols-2 md:block gap-2 md:gap-0">
        {TOP_ITEMS.map(({ key, label, Icon }) => {
          const active = filter === key;
          const isArchive = key === "completed";
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              data-testid={`filter-${key}`}
              className={`w-full flex items-center justify-between px-3 py-2.5 md:mb-1 border transition-colors ${
                active
                  ? isArchive
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-zinc-900 bg-zinc-900 text-white"
                  : "border-zinc-200 md:border-transparent text-zinc-700 hover:bg-zinc-50 hover:border-zinc-200"
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium">{label}</span>
              </span>
              <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>
                {counts[key] ?? 0}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-8 hidden md:block">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Lists</p>
          <button
            type="button"
            onClick={onCreateList}
            className="text-zinc-500 hover:text-zinc-900 p-1 -mr-1"
            title="New list"
            data-testid="sidebar-new-list-btn"
            aria-label="New list"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="space-y-1">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ownedLists.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {ownedLists.map((list) => (
                <SortableListRow
                  key={list.id}
                  list={list}
                  active={filter === `list:${list.id}`}
                  count={counts[`list:${list.id}`] ?? 0}
                  isRenaming={renamingId === list.id}
                  draftName={draftName}
                  setDraftName={setDraftName}
                  startRename={startRename}
                  commitRename={commitRename}
                  cancelRename={cancelRename}
                  menuOpen={menuOpenId === list.id}
                  setMenuOpenId={setMenuOpenId}
                  onSelect={() => setFilter(`list:${list.id}`)}
                  onUpdateList={onUpdateList}
                  onDeleteList={onDeleteList}
                  onShareList={onShareList}
                />
              ))}
            </SortableContext>
          </DndContext>
          <button
            type="button"
            onClick={onCreateList}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] font-medium text-zinc-500 hover:text-zinc-900 border border-dashed border-zinc-300 hover:border-zinc-900 transition-colors"
            data-testid="sidebar-new-list-cta"
          >
            <Plus className="w-3.5 h-3.5" />
            New list
          </button>
          {onShareWorkspace && (
            <button
              type="button"
              onClick={onShareWorkspace}
              className="w-full flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-medium text-zinc-400 hover:text-[#002FA7] transition-colors"
              data-testid="sidebar-share-workspace-btn"
            >
              <Users className="w-3 h-3" /> Share whole workspace
            </button>
          )}
        </div>
      </div>

      {sharedLists.length > 0 && (
        <div className="mt-8 hidden md:block" data-testid="sidebar-shared-with-me">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">Shared with me</p>
          <div className="space-y-1">
            {sharedLists.map((list) => {
              const active = filter === `list:${list.id}`;
              const CatIcon = list.category === "personal" ? User : Briefcase;
              return (
                <button
                  key={list.id}
                  type="button"
                  onClick={() => setFilter(`list:${list.id}`)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border transition-colors ${active ? "border-emerald-700 bg-emerald-700 text-white" : "border-transparent hover:bg-zinc-50 hover:border-zinc-200"}`}
                  data-testid={`shared-list-${list.id}`}
                >
                  <CatIcon className={`w-4 h-4 shrink-0 ${active ? "text-white/80" : "text-zinc-500"}`} strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${active ? "text-white" : "text-zinc-700"}`}>{list.name}</div>
                    <div className={`text-[10px] truncate ${active ? "text-white/70" : "text-zinc-500"}`}>
                      from {list.owner_name || list.owner_email} · {list.permission}
                    </div>
                  </div>
                  <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>{counts[`list:${list.id}`] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10 hidden md:block">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">
          Shortcuts
        </p>
        <ul className="space-y-2 text-xs text-zinc-600">
          <li className="flex items-center justify-between">
            <span>Add task</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-700 font-mono text-[10px]">/</kbd>
          </li>
          <li className="flex items-center justify-between">
            <span>Break it down</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-700 font-mono text-[10px]">B</kbd>
          </li>
          <li className="flex items-center justify-between">
            <span>Toggle timer</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-700 font-mono text-[10px]">SPACE</kbd>
          </li>
          <li className="flex items-center justify-between">
            <span>Exit focus</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-700 font-mono text-[10px]">ESC</kbd>
          </li>
        </ul>

        <div className="mt-8 pt-6 border-t border-zinc-200">
          <ShareAppLink medium="sidebar" />
        </div>
      </div>
    </aside>
  );
}

function SortableListRow({
  list,
  active,
  count,
  isRenaming,
  draftName,
  setDraftName,
  startRename,
  commitRename,
  cancelRename,
  menuOpen,
  setMenuOpenId,
  onSelect,
  onUpdateList,
  onDeleteList,
  onShareList,
}) {
  const CatIcon = list.category === "personal" ? User : Briefcase;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.id,
    disabled: isRenaming,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative border transition-colors ${
        active
          ? "border-zinc-900 bg-zinc-900 text-white"
          : "border-transparent hover:border-zinc-200"
      } ${isDragging ? "shadow-lg" : ""}`}
      data-testid={`list-row-${list.id}`}
    >
      {isRenaming ? (
        <div className="flex items-center gap-1 px-2.5 py-2">
          <CatIcon className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={1.5} />
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") cancelRename();
            }}
            onBlur={commitRename}
            maxLength={60}
            className="flex-1 min-w-0 text-sm bg-transparent outline-none border-b border-zinc-400 focus:border-zinc-900 text-zinc-900"
            data-testid={`list-rename-input-${list.id}`}
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commitRename}
            className="p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelRename}
            className="p-1 text-zinc-500 hover:bg-zinc-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-stretch">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className={`px-1.5 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity ${
              active ? "text-white/60" : "text-zinc-400"
            }`}
            aria-label={`Drag ${list.name} to reorder`}
            data-testid={`list-drag-${list.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onSelect}
            className="flex-1 flex items-center gap-2.5 px-1 py-2 text-left min-w-0"
            data-testid={`list-select-${list.id}`}
          >
            <CatIcon
              className={`w-4 h-4 shrink-0 ${active ? "text-white/80" : "text-zinc-500"}`}
              strokeWidth={1.5}
            />
            <span className={`text-sm font-medium flex-1 truncate ${active ? "text-white" : "text-zinc-700"}`}>
              {list.name}
            </span>
            <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>
              {count}
            </span>
          </button>
        </div>
      )}

      {!isRenaming && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpenId(menuOpen ? null : list.id);
            }}
            className={`opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-1 ${
              active ? "text-white hover:bg-white/10" : "text-zinc-500 hover:bg-zinc-100"
            }`}
            data-testid={`list-menu-btn-${list.id}`}
            aria-label="List options"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
              <div
                className="absolute right-0 top-7 z-20 bg-white border border-zinc-200 shadow-lg w-44 py-1"
                data-testid={`list-menu-${list.id}`}
              >
                <button
                  type="button"
                  onClick={() => startRename(list)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
                  data-testid={`list-rename-${list.id}`}
                >
                  <Pencil className="w-3 h-3" /> Rename
                </button>
                {onShareList && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpenId(null); onShareList(list); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
                    data-testid={`list-share-${list.id}`}
                  >
                    <Share2 className="w-3 h-3" /> Share list…
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpenId(null);
                    const next = list.category === "work" ? "personal" : "work";
                    if (window.confirm(`Change category to ${next}? All tasks in this list will be re-tagged as ${next}.`)) {
                      onUpdateList(list.id, { category: next });
                    }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
                  data-testid={`list-toggle-cat-${list.id}`}
                >
                  {list.category === "work" ? <User className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                  Move to {list.category === "work" ? "Personal" : "Work"}
                </button>
                {!list.is_default && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpenId(null);
                      onDeleteList(list);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 text-left"
                    data-testid={`list-delete-${list.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete list…
                  </button>
                )}
                {list.is_default && (
                  <div className="px-3 py-1.5 text-[10px] text-zinc-400 italic">
                    Default list (can't be deleted)
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
